import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Sidebar from "../components/Sidebar";
import { updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider, signOut, deleteUser } from "firebase/auth";
import { doc, updateDoc, deleteDoc, where, query, getDocs, collection, getDoc } from "firebase/firestore";
import { auth, db } from "../firebaseConfig";
import { 
    User, 
    Palette, 
    CreditCard, 
    Trash2, 
    LogOut, 
    Mail, 
    Lock, 
    Eye, 
    EyeOff,
    Check,
    AlertCircle,
    Settings as SettingsIcon,
    Bell,
    Shield,
    HelpCircle,
    ChevronRight,
    Save
} from "lucide-react";
import ToastContainer from "../components/ToastContainer";
import useToast from "../hooks/useToast";
import "../styles/Dashboard.css";
import { AuthContext } from "../context/AuthContext";
import { PLAN_LIMITS } from "../services/userService";
import { createPremiumCheckoutSession } from "../services/subscriptionService";
import { ALWAYS_PREMIUM } from "../config/featureFlags";

const AccountSettings = () => {
    const [activeSection, setActiveSection] = useState("personal-info");
    const [isLoading, setIsLoading] = useState(false);
    const [isSubscriptionProcessing, setIsSubscriptionProcessing] = useState(false);
    const navigate = useNavigate();
    const { toasts, removeToast, success, error: showError } = useToast();
    const { userProfile, profileLoading } = useContext(AuthContext);

    const handleLogout = async () => {
        try {
            setIsLoading(true);
            await signOut(auth);
            success("Déconnexion réussie");
            navigate("/login");
        } catch (error) {
            console.error("Erreur lors de la déconnexion :", error);
            showError("Erreur lors de la déconnexion");
        } finally {
            setIsLoading(false);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    const settingsSections = [
        { key: "personal-info", label: "Informations personnelles", icon: User, color: "blue" },
        { key: "preferences", label: "Préférences", icon: Palette, color: "purple" },
        { key: "notifications", label: "Notifications", icon: Bell, color: "green" },
        { key: "privacy", label: "Confidentialité", icon: Shield, color: "orange" },
        { key: "subscription", label: "Abonnement", icon: CreditCard, color: "indigo" },
        { key: "help", label: "Aide", icon: HelpCircle, color: "gray" }
    ];

    const handleUpgradeToPremium = async () => {
        if (!auth.currentUser) {
            showError("Veuillez vous reconnecter pour gérer votre abonnement.");
            return;
        }

        if (isSubscriptionProcessing) {
            return;
        }

        if (ALWAYS_PREMIUM) {
            success("Le mode Premium est déjà activé pour tous les comptes.");
            return;
        }

        setIsSubscriptionProcessing(true);
        try {
            const url = await createPremiumCheckoutSession({
                user: auth.currentUser,
                metadata: { source: "settings_subscription" },
            });
            window.location.href = url;
        } catch (error) {
            console.error("Erreur lors de l'ouverture du checkout Premium :", error);
            showError(error.message || "Impossible de lancer le paiement. Réessayez plus tard.");
            setIsSubscriptionProcessing(false);
        }
    };


    return (
        <div className="min-h-screen bg-white">
            <Sidebar />
            
            <main className="ml-0 md:ml-24 px-4 py-6 md:px-8 pb-20 md:pb-6">
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    className="max-w-7xl mx-auto"
                >
                    {/* Header */}
                    <motion.div variants={itemVariants} className="mb-8">
                        <div className="flex items-center space-x-3 mb-6">
                            <div className="p-3 bg-gray-100 rounded-xl">
                                <SettingsIcon className="w-6 h-6 text-gray-700" />
                            </div>
                            <div>
                                <h1 className="text-2xl md:text-4xl font-bold text-gray-900">
                                    Paramètres
                                </h1>
                                <p className="text-gray-600">
                                    Gérez vos informations et préférences
                                </p>
                            </div>
                        </div>
                    </motion.div>

                    {/* Contenu principal */}
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                        {/* Menu latéral */}
                        <motion.aside 
                            variants={itemVariants}
                            className="lg:col-span-1"
                        >
                            <div className="bg-white rounded-2xl p-6 shadow-soft sticky top-6">
                                <nav className="space-y-2">
                                    {settingsSections.map((section, index) => {
                                        const IconComponent = section.icon;
                                        const isActive = activeSection === section.key;
                                        return (
                                            <motion.button
                                                key={section.key}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                whileHover={{ scale: 1.02 }}
                                                whileTap={{ scale: 0.98 }}
                                                onClick={() => setActiveSection(section.key)}
                                                className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                                                    isActive
                                                        ? 'bg-black text-white shadow-medium'
                                                        : 'text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                <div className="flex items-center space-x-3">
                                                    <div className={`p-2 rounded-lg ${
                                                        isActive 
                                                            ? 'bg-white/20' 
                                                            : `bg-${section.color}-50`
                                                    }`}>
                                                        <IconComponent className={`w-5 h-5 ${
                                                            isActive 
                                                                ? 'text-white' 
                                                                : `text-${section.color}-600`
                                                        }`} />
                                                    </div>
                                                    <span className="font-medium">{section.label}</span>
                                                </div>
                                                <ChevronRight className={`w-4 h-4 ${
                                                    isActive ? 'text-white' : 'text-gray-400'
                                                }`} />
                                            </motion.button>
                                        );
                                    })}
                                    
                                    {/* Boutons d'action dangereux */}
                                    <div className="pt-4 border-t border-gray-100 mt-4">
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={() => setActiveSection("delete-account")}
                                            className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                                                activeSection === "delete-account"
                                                    ? 'bg-red-600 text-white shadow-medium'
                                                    : 'text-red-600 hover:bg-red-50'
                                            }`}
                                        >
                                            <div className="flex items-center space-x-3">
                                                <div className={`p-2 rounded-lg ${
                                                    activeSection === "delete-account" 
                                                        ? 'bg-white/20' 
                                                        : 'bg-red-50'
                                                }`}>
                                                    <Trash2 className="w-5 h-5" />
                                                </div>
                                                <span className="font-medium">Supprimer le compte</span>
                                            </div>
                                            <ChevronRight className="w-4 h-4" />
                                        </motion.button>
                                        
                                        <motion.button
                                            whileHover={{ scale: 1.02 }}
                                            whileTap={{ scale: 0.98 }}
                                            onClick={handleLogout}
                                            disabled={isLoading}
                                            className="w-full flex items-center space-x-3 p-4 rounded-xl transition-all text-red-600 hover:bg-red-50 disabled:opacity-50"
                                        >
                                            <div className="p-2 rounded-lg bg-red-50">
                                                <LogOut className="w-5 h-5" />
                                            </div>
                                            <span className="font-medium">
                                                {isLoading ? "Déconnexion..." : "Se déconnecter"}
                                            </span>
                                        </motion.button>
                                    </div>
                                </nav>
                            </div>
                        </motion.aside>

                        {/* Contenu dynamique */}
                        <motion.div 
                            variants={itemVariants}
                            className="lg:col-span-3"
                        >
                            <div className="bg-white rounded-2xl p-8 shadow-soft">
                                <AnimatePresence mode="wait">
                                    {activeSection === "personal-info" && (
                                        <motion.div
                                            key="personal-info"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <PersonalInfoSection />
                                        </motion.div>
                                    )}
                                    {activeSection === "preferences" && (
                                        <motion.div
                                            key="preferences"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <PreferencesSection />
                                        </motion.div>
                                    )}
                                    {activeSection === "notifications" && (
                                        <motion.div
                                            key="notifications"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <NotificationsSection />
                                        </motion.div>
                                    )}
                                    {activeSection === "privacy" && (
                                        <motion.div
                                            key="privacy"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <PrivacySection />
                                        </motion.div>
                                    )}
                                    {activeSection === "subscription" && (
                                        <motion.div
                                            key="subscription"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <SubscriptionSection
                                                userProfile={userProfile}
                                                profileLoading={profileLoading}
                                                onUpgrade={handleUpgradeToPremium}
                                                isProcessing={isSubscriptionProcessing}
                                            />
                                        </motion.div>
                                    )}
                                    {activeSection === "help" && (
                                        <motion.div
                                            key="help"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <HelpSection />
                                        </motion.div>
                                    )}
                                    {activeSection === "delete-account" && (
                                        <motion.div
                                            key="delete-account"
                                            initial={{ opacity: 0, x: 20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            exit={{ opacity: 0, x: -20 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <DeleteAccountSection />
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    </div>
                </motion.div>
            </main>
            
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </div>
    );
};

// Section : Informations personnelles
const PersonalInfoSection = () => {
    const [email, setEmail] = useState(auth.currentUser?.email || "");
    const [username, setUsername] = useState("");
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false
    });
    const { success: showSuccess, error: showError } = useToast();

    const validateEmail = (email) => {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    };

    useEffect(() => {
        const fetchUsername = async () => {
            const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
            if (userDoc.exists()) {
                setUsername(userDoc.data().username);
            }
        };
        fetchUsername();
    }, []);

    const reauthenticateUser = async (password) => {
        try {
            const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
            await reauthenticateWithCredential(auth.currentUser, credential);
            return true;
        } catch (error) {
            setError("Mot de passe actuel incorrect");
            return false;
        }
    };

    const handleEmailUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        if (!email || !currentPassword) {
            setError("Veuillez remplir tous les champs");
            setLoading(false);
            return;
        }

        if (!validateEmail(email)) {
            setError("Format d'email invalide");
            setLoading(false);
            return;
        }

        const isAuthenticated = await reauthenticateUser(currentPassword);
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }

        try {
            await updateEmail(auth.currentUser, email);
            await updateDoc(doc(db, "users", auth.currentUser.uid), { email });
            setSuccess("Email mis à jour avec succès !");
            showSuccess("Email mis à jour avec succès !");
            setEmail("");
            setCurrentPassword("");
        } catch (err) {
            setError(err.message);
            showError(err.message);
        }
        setLoading(false);
    };

    const handleUsernameUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        if (username.length < 3 || username.length > 20) {
            setError("Le nom d'utilisateur doit contenir entre 3 et 20 caractères");
            setLoading(false);
            return;
        }

        try {
            const q = query(collection(db, "users"), where("username", "==", username));
            const snapshot = await getDocs(q);

            if (!snapshot.empty && snapshot.docs[0].id !== auth.currentUser.uid) {
                throw new Error("Ce nom d'utilisateur est déjà pris");
            }

            await updateDoc(doc(db, "users", auth.currentUser.uid), { username });
            setSuccess("Nom d'utilisateur mis à jour !");
            showSuccess("Nom d'utilisateur mis à jour !");
            setUsername("");
        } catch (err) {
            setError(err.message);
            showError(err.message);
        }
        setLoading(false);
    };

    const handlePasswordUpdate = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setSuccess("");

        if (!currentPassword || !newPassword || !confirmPassword) {
            setError("Veuillez remplir tous les champs");
            setLoading(false);
            return;
        }

        if (newPassword.length < 6) {
            setError("Le mot de passe doit contenir au moins 6 caractères");
            setLoading(false);
            return;
        }

        if (newPassword !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas");
            setLoading(false);
            return;
        }

        const isAuthenticated = await reauthenticateUser(currentPassword);
        if (!isAuthenticated) {
            setLoading(false);
            return;
        }

        try {
            await updatePassword(auth.currentUser, newPassword);
            setSuccess("Mot de passe mis à jour !");
            showSuccess("Mot de passe mis à jour !");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err) {
            setError(err.message);
            showError(err.message);
        }
        setLoading(false);
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-blue-50 rounded-xl">
                    <User className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Informations personnelles</h2>
                    <p className="text-gray-600">Gérez vos informations de compte</p>
                </div>
            </div>

            {/* Formulaire Email */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gray-50 rounded-2xl p-6"
            >
                <div className="flex items-center space-x-3 mb-4">
                    <Mail className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Modifier l'email</h3>
                </div>
                <form onSubmit={handleEmailUpdate} className="space-y-4">
                    <div>
                        <input
                            type="email"
                            placeholder="Nouvel email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                    </div>
                    <div className="relative">
                        <input
                            type={showPasswords.current ? "text" : "password"}
                            placeholder="Mot de passe actuel"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                        className="w-full bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Mise à jour...</span>
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                <span>Mettre à jour l'email</span>
                            </>
                        )}
                    </motion.button>
                </form>
            </motion.div>

            {/* Formulaire Username */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gray-50 rounded-2xl p-6"
            >
                <div className="flex items-center space-x-3 mb-4">
                    <User className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Modifier le nom d'utilisateur</h3>
                </div>
                <form onSubmit={handleUsernameUpdate} className="space-y-4">
                    <div>
                        <input
                            type="text"
                            placeholder="Nouveau nom d'utilisateur"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                    </div>
                    <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                        className="w-full bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Mise à jour...</span>
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                <span>Mettre à jour le nom d'utilisateur</span>
                            </>
                        )}
                    </motion.button>
                </form>
            </motion.div>

            {/* Formulaire Mot de passe */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gray-50 rounded-2xl p-6"
            >
                <div className="flex items-center space-x-3 mb-4">
                    <Lock className="w-5 h-5 text-gray-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Modifier le mot de passe</h3>
                </div>
                <form onSubmit={handlePasswordUpdate} className="space-y-4">
                    <div className="relative">
                        <input
                            type={showPasswords.current ? "text" : "password"}
                            placeholder="Mot de passe actuel"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    <div className="relative">
                        <input
                            type={showPasswords.new ? "text" : "password"}
                            placeholder="Nouveau mot de passe"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    <div className="relative">
                        <input
                            type={showPasswords.confirm ? "text" : "password"}
                            placeholder="Confirmer le nouveau mot de passe"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-4 py-3 pr-12 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                            {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>
                    <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                        className="w-full bg-black text-white px-6 py-3 rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Mise à jour...</span>
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                <span>Mettre à jour le mot de passe</span>
                            </>
                        )}
                    </motion.button>
                </form>
            </motion.div>

            {/* Messages d'erreur et de succès */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center space-x-3"
                    >
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="text-red-700">{error}</span>
                    </motion.div>
                )}
                {success && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 bg-green-50 border border-green-200 rounded-xl flex items-center space-x-3"
                    >
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="text-green-700">{success}</span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};


// Section : Préférences vestimentaires
const PreferencesSection = () => {
    const [selectedColors, setSelectedColors] = useState([]);
    const [selectedStyles, setSelectedStyles] = useState([]);
    const [selectedSizes, setSelectedSizes] = useState([]);
    const [loading, setLoading] = useState(true);
    const { success, error: showError } = useToast();

    // Charger les préférences depuis Firestore
    useEffect(() => {
        const loadPreferences = async () => {
            if (!auth.currentUser) return;
            try {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    setSelectedColors(data.preferences?.colors || []);
                    setSelectedStyles(data.preferences?.styles || []);
                    setSelectedSizes(data.preferences?.sizes || []);
                }
            } catch (error) {
                console.error("Erreur lors du chargement des préférences:", error);
            } finally {
                setLoading(false);
            }
        };
        loadPreferences();
    }, []);

    // Sauvegarder dans Firestore
    const savePreferences = async (colors, styles, sizes) => {
        if (!auth.currentUser) return;
        try {
            await updateDoc(doc(db, "users", auth.currentUser.uid), {
                preferences: {
                    colors,
                    styles,
                    sizes
                }
            });
        } catch (error) {
            console.error("Erreur lors de la sauvegarde:", error);
            showError("Erreur lors de la sauvegarde");
        }
    };

    const colors = [
        { name: "Rouge", value: "#EF4444" },
        { name: "Bleu", value: "#3B82F6" },
        { name: "Vert", value: "#10B981" },
        { name: "Jaune", value: "#F59E0B" },
        { name: "Violet", value: "#8B5CF6" },
        { name: "Rose", value: "#EC4899" },
        { name: "Orange", value: "#F97316" },
        { name: "Noir", value: "#1F2937" }
    ];

    const styles = ["Décontracté", "Chic", "Sport", "Élégant", "Vintage", "Moderne"];
    const sizes = ["XS", "S", "M", "L", "XL", "XXL"];

    const toggleColor = async (color) => {
        const newColors = selectedColors.includes(color) 
            ? selectedColors.filter(c => c !== color)
            : [...selectedColors, color];
        setSelectedColors(newColors);
        await savePreferences(newColors, selectedStyles, selectedSizes);
        success("Couleur sauvegardée");
    };

    const toggleStyle = async (style) => {
        const newStyles = selectedStyles.includes(style) 
            ? selectedStyles.filter(s => s !== style)
            : [...selectedStyles, style];
        setSelectedStyles(newStyles);
        await savePreferences(selectedColors, newStyles, selectedSizes);
        success("Style sauvegardé");
    };

    const toggleSize = async (size) => {
        const newSizes = selectedSizes.includes(size) 
            ? selectedSizes.filter(s => s !== size)
            : [...selectedSizes, size];
        setSelectedSizes(newSizes);
        await savePreferences(selectedColors, selectedStyles, newSizes);
        success("Taille sauvegardée");
    };

    if (loading) {
        return (
            <div className="space-y-8 animate-pulse">
                <div className="h-8 w-64 bg-gray-200 rounded"></div>
                <div className="h-32 bg-gray-100 rounded-2xl"></div>
                <div className="h-32 bg-gray-100 rounded-2xl"></div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-purple-50 rounded-xl">
                    <Palette className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Préférences vestimentaires</h2>
                    <p className="text-gray-600">Personnalisez vos goûts et styles</p>
                </div>
            </div>

            {/* Couleurs préférées */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-gray-50 rounded-2xl p-6"
            >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Couleurs préférées</h3>
                <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
                    {colors.map((color, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => toggleColor(color.value)}
                            className={`relative w-12 h-12 rounded-full cursor-pointer transition-all ${
                                selectedColors.includes(color.value) 
                                    ? 'ring-4 ring-gray-300 ring-offset-2' 
                                    : 'hover:ring-2 hover:ring-gray-200'
                            }`}
                            style={{ backgroundColor: color.value }}
                        >
                            {selectedColors.includes(color.value) && (
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    className="absolute inset-0 flex items-center justify-center"
                                >
                                    <Check className="w-6 h-6 text-white drop-shadow-lg" />
                                </motion.div>
                            )}
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Styles favoris */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-gray-50 rounded-2xl p-6"
            >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Styles favoris</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {styles.map((style, index) => (
                        <motion.button
                            key={index}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => toggleStyle(style)}
                            className={`p-4 rounded-xl border-2 transition-all ${
                                selectedStyles.includes(style)
                                    ? 'border-black bg-black text-white'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            <span className="font-medium">{style}</span>
                        </motion.button>
                    ))}
                </div>
            </motion.div>

            {/* Tailles standards */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-gray-50 rounded-2xl p-6"
            >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Tailles standards</h3>
                <div className="flex flex-wrap gap-3">
                    {sizes.map((size, index) => (
                        <motion.button
                            key={index}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.05 }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => toggleSize(size)}
                            className={`w-12 h-12 rounded-xl border-2 transition-all flex items-center justify-center font-semibold ${
                                selectedSizes.includes(size)
                                    ? 'border-black bg-black text-white'
                                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                            }`}
                        >
                            {size}
                        </motion.button>
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

// Section : Notifications
const NotificationsSection = () => {
    const [notifications, setNotifications] = useState({
        email: true,
        push: false,
        outfitReminders: true,
        styleTips: true,
        promotions: false
    });
    const [loading, setLoading] = useState(true);
    const { success, error: showError } = useToast();

    // Charger les notifications depuis Firestore
    useEffect(() => {
        const loadNotifications = async () => {
            if (!auth.currentUser) return;
            try {
                const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
                if (userDoc.exists()) {
                    const data = userDoc.data();
                    if (data.notifications) {
                        setNotifications(data.notifications);
                    }
                }
            } catch (error) {
                console.error("Erreur lors du chargement des notifications:", error);
            } finally {
                setLoading(false);
            }
        };
        loadNotifications();
    }, []);

    const toggleNotification = async (key) => {
        const newNotifications = {
            ...notifications,
            [key]: !notifications[key]
        };
        setNotifications(newNotifications);
        
        if (!auth.currentUser) return;
        try {
            await updateDoc(doc(db, "users", auth.currentUser.uid), {
                notifications: newNotifications
            });
            success("Notification mise à jour");
        } catch (error) {
            console.error("Erreur lors de la sauvegarde:", error);
            showError("Erreur lors de la sauvegarde");
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-green-50 rounded-xl">
                    <Bell className="w-6 h-6 text-green-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
                    <p className="text-gray-600">Gérez vos préférences de notification</p>
                </div>
            </div>

            <div className="space-y-4">
                {[
                    { key: 'email', label: 'Notifications par email', description: 'Recevez des mises à jour par email' },
                    { key: 'push', label: 'Notifications push', description: 'Recevez des notifications sur votre appareil' },
                    { key: 'outfitReminders', label: 'Rappels de tenues', description: 'Rappels pour créer vos tenues quotidiennes' },
                    { key: 'styleTips', label: 'Conseils de style', description: 'Conseils personnalisés et tendances' },
                    { key: 'promotions', label: 'Offres et promotions', description: 'Nouvelles offres et réductions' }
                ].map((item, index) => (
                    <motion.div
                        key={item.key}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl"
                    >
                        <div>
                            <h3 className="font-medium text-gray-900">{item.label}</h3>
                            <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                        <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={() => toggleNotification(item.key)}
                            className={`relative w-12 h-6 rounded-full transition-colors ${
                                notifications[item.key] ? 'bg-black' : 'bg-gray-300'
                            }`}
                        >
                            <motion.div
                                animate={{ x: notifications[item.key] ? 24 : 0 }}
                                className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm"
                            />
                        </motion.button>
                    </motion.div>
                ))}
            </div>
        </div>
    );
};

// Section : Confidentialité
const PrivacySection = () => {
    const [isDownloading, setIsDownloading] = useState(false);
    const { success, error: showError } = useToast();

    const downloadMyData = async () => {
        if (!auth.currentUser) {
            showError("Vous devez être connecté");
            return;
        }

        setIsDownloading(true);
        try {
            // Récupérer les données utilisateur
            const userDoc = await getDoc(doc(db, "users", auth.currentUser.uid));
            const userData = userDoc.exists() ? userDoc.data() : {};

            // Récupérer tous les vêtements
            const clothesQuery = query(collection(db, "clothes"), where("userId", "==", auth.currentUser.uid));
            const clothesSnapshot = await getDocs(clothesQuery);
            const clothes = clothesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Récupérer toutes les tenues
            const outfitsQuery = query(collection(db, "outfits"), where("userId", "==", auth.currentUser.uid));
            const outfitsSnapshot = await getDocs(outfitsQuery);
            const outfits = outfitsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Créer l'objet de données complet
            const allData = {
                exportDate: new Date().toISOString(),
                user: {
                    uid: auth.currentUser.uid,
                    email: auth.currentUser.email,
                    ...userData
                },
                clothes,
                outfits,
                stats: {
                    totalClothes: clothes.length,
                    totalOutfits: outfits.length
                }
            };

            // Créer et télécharger le fichier JSON
            const dataStr = JSON.stringify(allData, null, 2);
            const dataBlob = new Blob([dataStr], { type: "application/json" });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `dresscode-data-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            success("Données téléchargées avec succès");
        } catch (error) {
            console.error("Erreur lors du téléchargement des données:", error);
            showError("Erreur lors du téléchargement");
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-orange-50 rounded-xl">
                    <Shield className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Confidentialité</h2>
                    <p className="text-gray-600">Gérez vos données et votre vie privée</p>
                </div>
            </div>

            <div className="space-y-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gray-50 rounded-2xl p-6"
                >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Données personnelles</h3>
                    <p className="text-gray-600 mb-4">
                        Vos données sont protégées et ne sont jamais partagées avec des tiers.
                        Téléchargez une copie complète de toutes vos données (profil, vêtements, tenues).
                    </p>
                    <motion.button
                        whileHover={{ scale: isDownloading ? 1 : 1.02 }}
                        whileTap={{ scale: isDownloading ? 1 : 0.98 }}
                        onClick={downloadMyData}
                        disabled={isDownloading}
                        className="px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    >
                        {isDownloading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Téléchargement...</span>
                            </>
                        ) : (
                            <span>Télécharger mes données</span>
                        )}
                    </motion.button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="bg-gray-50 rounded-2xl p-6"
                >
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Paramètres de confidentialité</h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-gray-700">Profil public</span>
                            <div className="w-12 h-6 bg-gray-300 rounded-full"></div>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-700">Partage d'activité</span>
                            <div className="w-12 h-6 bg-black rounded-full"></div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

// Section : Aide
const HelpSection = () => {
    const faqs = [
        {
            question: "Comment créer une tenue ?",
            answer: "Utilisez la page Outfits pour générer automatiquement des tenues avec l'IA."
        },
        {
            question: "Comment ajouter des vêtements ?",
            answer: "Allez dans la page Dressing et cliquez sur le bouton 'Ajouter un vêtement'."
        },
        {
            question: "Comment modifier mes préférences ?",
            answer: "Dans cette page Paramètres, vous pouvez personnaliser vos goûts et styles."
        }
    ];

    return (
        <div className="space-y-8">
            <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-gray-50 rounded-xl">
                    <HelpCircle className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Aide et Support</h2>
                    <p className="text-gray-600">Trouvez des réponses à vos questions</p>
                </div>
            </div>

            <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900">Questions fréquentes</h3>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-gray-50 rounded-xl p-6"
                    >
                        <h4 className="font-medium text-gray-900 mb-2">{faq.question}</h4>
                        <p className="text-gray-600">{faq.answer}</p>
                    </motion.div>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-gray-50 rounded-2xl p-6"
            >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Besoin d'aide ?</h3>
                <p className="text-gray-600 mb-4">
                    Notre équipe support est là pour vous aider.
                </p>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-6 py-3 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
                >
                    Contacter le support
                </motion.button>
            </motion.div>
        </div>
    );
};

// Section : Abonnement
const SubscriptionSection = ({ userProfile, profileLoading, onUpgrade, isProcessing }) => {
    const plan = userProfile?.plan || "free";
    const isPremium = plan === "premium";
    const limits = userProfile?.limits || PLAN_LIMITS[plan] || PLAN_LIMITS.free;
    const planLimits = PLAN_LIMITS[plan] || {};
    // Conserver null (illimité) et ne faire un fallback que si undefined
    let dailyLimit;
    if (ALWAYS_PREMIUM) {
        dailyLimit = null;
    } else if (limits && Object.prototype.hasOwnProperty.call(limits, 'dailyGenerations')) {
        dailyLimit = limits.dailyGenerations; // peut être null
    } else if (Object.prototype.hasOwnProperty.call(planLimits, 'dailyGenerations')) {
        dailyLimit = planLimits.dailyGenerations; // peut être null
    } else {
        dailyLimit = PLAN_LIMITS.free.dailyGenerations;
    }
    const usage = userProfile?.generationUsage;
    const todayKey = new Date().toISOString().split("T")[0];
    const usageToday = usage?.date === todayKey ? usage.count || 0 : 0;
    const remainingGenerations = dailyLimit === null ? null : Math.max(dailyLimit - usageToday, 0);
    const statusLabel = (userProfile?.subscriptionStatus || plan || "free").toString();

    const updatedAtDate = userProfile?.updatedAt?.toDate
        ? userProfile.updatedAt.toDate()
        : userProfile?.updatedAt instanceof Date
            ? userProfile.updatedAt
            : null;

    const planBenefits = useMemo(() => {
        if (isPremium) {
            return [
                "Garde-robe illimitée",
                "Suggestions IA avancées et illimitées",
                "Statistiques détaillées",
                "Support prioritaire 24/7",
                "Accès anticipé aux nouveautés",
            ];
        }
        return [
            "Jusqu'à 20 vêtements enregistrés",
            "1 génération IA par jour",
            "Accès aux fonctionnalités essentielles",
            "Support par email sous 48h",
        ];
    }, [isPremium]);

    const cardGradient = isPremium
        ? "from-emerald-500 to-blue-600"
        : "from-indigo-500 to-purple-600";

    if (profileLoading && !userProfile) {
        return (
            <div className="space-y-8">
                <div className="flex items-center space-x-3 mb-8">
                    <div className="p-3 bg-indigo-50 rounded-xl animate-pulse" />
                    <div className="space-y-2">
                        <div className="h-5 w-40 bg-gray-200 rounded-md animate-pulse" />
                        <div className="h-4 w-56 bg-gray-100 rounded-md animate-pulse" />
                    </div>
                </div>
                <div className="bg-gray-100 rounded-2xl p-8 animate-pulse space-y-6">
                    <div className="h-6 w-48 bg-white/60 rounded-md" />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {[...Array(3)].map((_, index) => (
                            <div key={index} className="h-24 bg-white/50 rounded-xl" />
                        ))}
                    </div>
                    <div className="h-10 w-48 bg-white/70 rounded-xl" />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-indigo-50 rounded-xl">
                    <CreditCard className="w-6 h-6 text-indigo-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Abonnement</h2>
                    <p className="text-gray-600">Gérez votre plan et vos limites d'utilisation</p>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`bg-gradient-to-r ${cardGradient} rounded-2xl p-8 text-white shadow-lg`}
            >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div>
                        <p className="uppercase text-white/70 text-xs tracking-[0.3em] mb-2">
                            Plan actuel
                        </p>
                        <h3 className="text-3xl font-extrabold mb-1">
                            {isPremium ? "Premium" : "Gratuit"}
                        </h3>
                        <p className="text-white/80">
                            Statut : <span className="font-semibold capitalize">{statusLabel}</span>
                        </p>
                        {updatedAtDate && (
                            <p className="text-white/60 text-sm mt-2">
                                Mis à jour le {updatedAtDate.toLocaleDateString("fr-FR", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                })}
                            </p>
                        )}
                    </div>

                    <div className="bg-white/15 border border-white/20 rounded-2xl p-4 flex-1 min-w-[220px]">
                        <p className="text-sm text-white/70 mb-1">Générations IA aujourd'hui</p>
                        {dailyLimit === null ? (
                            <p className="text-xl font-semibold">Illimitées</p>
                        ) : (
                            <>
                                <p className="text-2xl font-semibold mb-2">
                                    {usageToday}/{dailyLimit}
                                </p>
                                <div className="h-2 w-full bg-white/20 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-white rounded-full transition-all"
                                        style={{
                                            width: `${Math.min((usageToday / dailyLimit) * 100, 100)}%`,
                                        }}
                                    />
                                </div>
                                <p className="text-xs text-white/70 mt-1">
                                    {remainingGenerations === null
                                        ? "Générations illimitées disponibles"
                                        : `Encore ${remainingGenerations} génération${
                                              remainingGenerations > 1 ? "s" : ""
                                          } disponible${
                                              remainingGenerations > 1 ? "s" : ""
                                          } aujourd'hui`}
                                </p>
                            </>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
                    {planBenefits.map((benefit, index) => (
                        <div key={index} className="bg-white/15 border border-white/10 rounded-xl p-4">
                            <h4 className="font-semibold text-white mb-1 flex items-center space-x-2">
                                <Check className="w-4 h-4 text-white/80" />
                                <span>{benefit}</span>
                            </h4>
                        </div>
                    ))}
                </div>

                <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="text-white/70 text-sm">
                        {userProfile?.stripeSubscriptionId ? (
                            <>
                                <p>ID abonnement Stripe : {userProfile.stripeSubscriptionId}</p>
                                <p>ID client : {userProfile.stripeCustomerId || "—"}</p>
                            </>
                        ) : (
                            <p>
                                Besoin d'encore plus de fonctionnalités ? Passez au Premium pour débloquer toutes les
                                capacités de DressCode.
                            </p>
                        )}
                    </div>

                    {isPremium ? (
                        <div className="flex flex-col items-start md:items-end text-white/70 text-sm">
                            <p>Envie de modifier ou résilier ?</p>
                            <p>Le portail de gestion des abonnements arrive très bientôt.</p>
                            <p>En attendant, contactez le support.</p>
                        </div>
                    ) : (
                        <motion.button
                            whileHover={{ scale: isProcessing ? 1 : 1.02 }}
                            whileTap={{ scale: isProcessing ? 1 : 0.98 }}
                            disabled={isProcessing}
                            onClick={onUpgrade}
                            className="bg-white text-indigo-600 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {isProcessing ? "Redirection..." : "Passer au plan Premium"}
                        </motion.button>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

// Section : Supprimer le compte
const DeleteAccountSection = () => {
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const { success: showSuccess, error: showError } = useToast();

    const handleDeleteAccount = async (e) => {
        e.preventDefault();
        if (!confirmDelete) {
            setError("Veuillez confirmer la suppression en cochant la case");
            return;
        }

        setLoading(true);
        setError("");
        setSuccess("");

        try {
            // Réauthentification
            const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
            await reauthenticateWithCredential(auth.currentUser, credential);

            // Suppression des données Firestore
            await deleteDoc(doc(db, "users", auth.currentUser.uid));

            // Suppression du compte Firebase
            await deleteUser(auth.currentUser);

            setSuccess("Compte supprimé avec succès !");
            showSuccess("Compte supprimé avec succès !");
            // Rediriger l'utilisateur vers la page d'accueil ou de connexion
            // Par exemple : navigate('/');
        } catch (err) {
            console.error("Erreur lors de la suppression du compte:", err);
            setError(err.message);
            showError(err.message);
        }
        setLoading(false);
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center space-x-3 mb-8">
                <div className="p-3 bg-red-50 rounded-xl">
                    <Trash2 className="w-6 h-6 text-red-600" />
                </div>
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Supprimer le compte</h2>
                    <p className="text-gray-600">Cette action est irréversible</p>
                </div>
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 rounded-2xl p-8"
            >
                <div className="flex items-start space-x-3 mb-6">
                    <AlertCircle className="w-6 h-6 text-red-600 mt-1" />
                    <div>
                        <h3 className="text-lg font-semibold text-red-900 mb-2">
                            Attention : Action irréversible
                        </h3>
                        <p className="text-red-700">
                            La suppression de votre compte entraînera la perte définitive de toutes vos données :
                        </p>
                        <ul className="list-disc list-inside text-red-700 mt-2 space-y-1">
                            <li>Tous vos vêtements et tenues</li>
                            <li>Vos préférences et paramètres</li>
                            <li>Votre historique d'activité</li>
                            <li>Toutes les données associées à votre compte</li>
                        </ul>
                    </div>
                </div>

                <form onSubmit={handleDeleteAccount} className="space-y-6">
                    <div className="relative">
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Mot de passe actuel"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-4 py-3 pr-12 border border-red-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-400 hover:text-red-600"
                        >
                            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                        </button>
                    </div>

                    <div className="flex items-start space-x-3">
                        <input
                            type="checkbox"
                            id="confirmDelete"
                            checked={confirmDelete}
                            onChange={(e) => setConfirmDelete(e.target.checked)}
                            className="mt-1 w-5 h-5 text-red-600 border-red-300 rounded focus:ring-red-500"
                        />
                        <label htmlFor="confirmDelete" className="text-red-700 text-sm">
                            Je comprends que cette action est irréversible et que toutes mes données seront définitivement supprimées.
                        </label>
                    </div>

                    <motion.button
                        type="submit"
                        disabled={loading || !confirmDelete || !password}
                        whileHover={{ scale: loading ? 1 : 1.02 }}
                        whileTap={{ scale: loading ? 1 : 0.98 }}
                        className="w-full bg-red-600 text-white px-6 py-3 rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Suppression en cours...</span>
                            </>
                        ) : (
                            <>
                                <Trash2 className="w-5 h-5" />
                                <span>Supprimer définitivement mon compte</span>
                            </>
                        )}
                    </motion.button>
                </form>

                <AnimatePresence>
                    {error && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mt-4 p-4 bg-red-100 border border-red-300 rounded-xl flex items-center space-x-3"
                        >
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <span className="text-red-700">{error}</span>
                        </motion.div>
                    )}
                    {success && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="mt-4 p-4 bg-green-100 border border-green-300 rounded-xl flex items-center space-x-3"
                        >
                            <Check className="w-5 h-5 text-green-600" />
                            <span className="text-green-700">{success}</span>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};


export default AccountSettings;
